import { supabase } from '../lib/supabaseClient';

// =============================================================
// ログDBへの送信キュー（設計：research/20261010_操作ログDB設計指針.md の4章）
//
// 外部キーのある表（attempts → step_responses → option_selections など）へ、
// ネットワークが切れても記録を失わず・二重にもせずに送るための仕組み。
//   1. 行の ID はブラウザで作る（utils/uuid.js）。親を送る前から子が親の ID を持てる
//   2. 送信待ちは1本のキューにし、作った順に1行ずつ送る（親→子の順が崩れない）
//   3. 送れなかったらその行で止め、後続を待たせる（次の記録時・オンライン復帰時・数秒後に再開）
//   4. 自分の ID（主キー・event_id）の重複エラー（23505）は「保存済み」とみなして外す
//      （応答だけ失われた再送で二重にしない）
//   5. 親が無いエラー（23503）は待って再送。行そのものが不正（型・制約違反、ID 以外の一意制約）なら
//      送り直しても通らないので、端末内の「送れなかった記録」（DEAD_KEY）へ移して後で回収できるようにする
//   6. **キューを止めない**（2026-10-12）：送れなかった行を回収用へ移すときは、その行を親に持つ後続の行
//      （回答に対する空欄ごとの選択など）も一緒に移す。親待ちの再送にも上限（MAX_PARENT_WAITS）を設け、
//      親が来なければ回収用へ移す。旧方式では、同じ設問への2回目の回答が一意制約で外されたあと
//      その空欄の行が親待ちのまま先頭で止まり、その端末の以後の記録が一切届かなくなっていた
// キューは localStorage に置くので、リロードしても未送信分は残る。アプリを開いた時点でも送り始める。
// =============================================================

const QUEUE_KEY = 'experimentLogQueueV2';
const LEGACY_QUEUE_KEY = 'experimentLogQueue'; // 2026-10-10 以前（experiment_logs だけの配列）
const DEAD_KEY = 'experimentLogDeadLetter';
const MAX_QUEUE_LENGTH = 3000;  // これを超えたら取れたら取る記録（WANT）から捨てる
const RETRY_DELAY_MS = 5000;
const MAX_PARENT_WAITS = 12;    // 親が無い（23503）ときに待つ回数（RETRY_DELAY_MS ごと＝約1分）
const OPTIONAL_TABLES = ['camera_logs'];

let flushing = false;
let retryTimer = null;
let seq = 0;

function read(key) {
  try {
    const arr = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch (_) {
    return [];
  }
}

// 送信待ちの件数が変わったら知らせる相手（完了画面の「送っています」表示）
const listeners = new Set();

function write(key, arr) {
  try {
    localStorage.setItem(key, JSON.stringify(arr));
  } catch (_) {
    // localStorage が使えない・満杯の環境では諦める（演習は止めない）
  }
  if (key === QUEUE_KEY) listeners.forEach((fn) => { try { fn(arr.length); } catch (_) { /* 表示側の不具合で送信は止めない */ } });
}

/** 送信待ちの件数（ログ送信が無効な環境では null）。 */
export function pendingCount() {
  return supabase ? read(QUEUE_KEY).length : null;
}

/** 送信待ちの件数が変わるたびに fn(件数) を呼ぶ。戻り値で解除する。 */
export function subscribePending(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// 旧形式のキューに残っていた未送信分を新しいキューの先頭へ移す
function migrateLegacy() {
  const legacy = read(LEGACY_QUEUE_KEY);
  if (legacy.length === 0) return;
  const items = legacy.map((row) => ({ qid: `legacy-${seq++}-${Date.now()}`, table: 'experiment_logs', row }));
  write(QUEUE_KEY, [...items, ...read(QUEUE_KEY)]);
  try { localStorage.removeItem(LEGACY_QUEUE_KEY); } catch (_) { /* 次回また移す */ }
}

// 23505 が自分の ID（主キー・event_id）の重複か。そうなら応答だけ失われた再送で、既に保存されている。
// それ以外の一意制約（例：旧スキーマの「同じ設問×ステップは1回まで」）にかかった行は保存されていない
function isOwnIdDuplicate(error) {
  const text = `${error.message || ''} ${error.details || ''}`;
  if (!/\S/.test(text)) return true; // どの制約か分からないときは従来どおり保存済みとみなす
  return /_pkey|event_id_key|\((response_id|attempt_id|event_id|id)\)=/.test(text);
}

/** 送信エラーを、外す（saved）／待つ（retry）／回収用へ移す（dead）に分ける。 */
export function classifyError(error) {
  const code = (error && error.code) || '';
  if (code === '23505') return isOwnIdDuplicate(error) ? 'saved' : 'dead';
  if (code === '23503') return 'retry';
  if (/^(22|23)/.test(code)) return 'dead';
  // 通信断（code なし）・権限・表が無い（SQL 未実行）などは直れば通るので待つ
  return 'retry';
}

function removeHead(qid) {
  write(QUEUE_KEY, read(QUEUE_KEY).filter((item) => item.qid !== qid));
}

// item が parent を親に持つ行か（parent が届かないなら item も届かない）
function dependsOn(item, parent) {
  if (parent.table === 'attempts') return item.row && item.row.attempt_id === parent.row.attempt_id;
  if (parent.table === 'step_responses') return item.table === 'option_selections' && item.row.response_id === parent.row.response_id;
  return false;
}

// 送れなかった行を、その行を親に持つ後続の行と一緒に回収用へ移す（キューを止めない）
function moveToDead(head, error) {
  const queue = read(QUEUE_KEY);
  const children = queue.filter((item) => item.qid !== head.qid && dependsOn(item, head));
  const childError = { code: 'parent-dead', message: `親の行（${head.table}）が送れなかったため` };
  write(DEAD_KEY, [...read(DEAD_KEY), { ...head, error }, ...children.map((c) => ({ ...c, error: childError }))]);
  const drop = new Set([head.qid, ...children.map((c) => c.qid)]);
  write(QUEUE_KEY, queue.filter((item) => !drop.has(item.qid)));
}

// 親待ちの回数を1つ増やして返す
function countParentWait(qid) {
  const queue = read(QUEUE_KEY);
  const item = queue.find((i) => i.qid === qid);
  if (!item) return 0;
  item.parentWaits = (item.parentWaits || 0) + 1;
  write(QUEUE_KEY, queue);
  return item.parentWaits;
}

function scheduleRetry() {
  if (retryTimer) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    flushQueue();
  }, RETRY_DELAY_MS);
}

/** キューの先頭から順に送る。止まったら後続は送らない。 */
export async function flushQueue() {
  if (!supabase || flushing) return;
  flushing = true;
  try {
    migrateLegacy();
    for (;;) {
      const [head] = read(QUEUE_KEY);
      if (!head) break;
      let error = null;
      try {
        ({ error } = await supabase.from(head.table).insert(head.row));
      } catch (e) {
        error = { code: '', message: String((e && e.message) || e) }; // 通信断など
      }
      let result = error ? classifyError(error) : 'saved';
      // 親が来ないまま待ち続けると後続がすべて止まるので、上限を超えたら回収用へ移す
      if (result === 'retry' && error.code === '23503' && countParentWait(head.qid) >= MAX_PARENT_WAITS) result = 'dead';
      if (result === 'retry') {
        scheduleRetry();
        break;
      }
      if (result === 'dead') moveToDead(head, error);
      else removeHead(head.qid);
    }
  } finally {
    flushing = false;
  }
}

/** 1行をキューの末尾に積んで送信を始める（投げっぱなし。UI は待たない）。 */
export function enqueue(table, row) {
  if (!supabase) return;
  const queue = read(QUEUE_KEY);
  if (queue.length >= MAX_QUEUE_LENGTH && OPTIONAL_TABLES.includes(table)) return;
  queue.push({ qid: `${Date.now()}-${seq++}`, table, row });
  write(QUEUE_KEY, queue);
  flushQueue();
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { flushQueue(); });
  // アプリを開いた時点でも、前回送り切れなかった分を送る（送信途中でタブを閉じた・通信が切れたまま終えた場合）。
  // すでに送信に失敗して再送を待っているなら、その再送に任せる（すぐ送り直しても同じように失敗するため）
  setTimeout(() => { if (!retryTimer) flushQueue(); }, 0);
}
